import { Link } from 'react-router-dom';
import GoldRule from '../components/GoldRule';
import { PRODUCTS, getProductImage } from '../data/products';
import ImageMarquee from '../components/motion/ImageMarquee';
import LeatherBackdrop from '../components/LeatherBackdrop';
import TrustedByMarquee from '../components/TrustedByMarquee';
import { Reveal, RevealLines, Stagger, StaggerItem } from '../components/motion/Reveal';

export default function About() {
  const craftItems = Object.values(PRODUCTS)
    .slice(0, 10)
    .map((p) => ({ code: p.code, name: p.name, image: getProductImage(p.code) }));

  return (
    <div data-testid="about-page">
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
        <Reveal y={12}>
          <div className="eyebrow text-mb-muted mb-8">On the house</div>
        </Reveal>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-mb leading-[0.95]">
          <RevealLines lines={['A house of']} />
          <RevealLines lines={['curated leather.']} delay={0.1} lineClassName="italic" />
        </h1>
        <Reveal delay={0.32} className="mt-10">
          <GoldRule size="med" />
        </Reveal>
        <Reveal delay={0.4}>
          <p className="mt-10 font-display italic text-[18px] md:text-[22px] text-mb-muted leading-relaxed max-w-2xl">
            MR. BOSS &amp; CO. is a small atelier that works in Italian and
            Nappa leather, exclusively for corporate gifting.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pb-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <h2 className="font-display text-[18px] leading-[1.7] text-mb">
              <RevealLines
                lines={[
                  'We do not sell individually, we do not ship',
                  'direct-to-consumer, and we do not chase seasons.',
                  'We present four sets — each cut for a specific',
                  'moment in professional life — and we repeat them,',
                  'slowly, for the clients who understand the',
                  'difference between gifts and ceremony.',
                ]}
              />
            </h2>
          </div>
          <Reveal delay={0.16} className="md:col-span-6">
            <p className="text-[14px] leading-[1.8] text-mb-muted">
              Every piece is saddle-stitched by hand, edge-painted four times,
              and inspected by a single pair of eyes before being placed in
              its set. Packaging is cloth-wrapped or wooden, always
              proportional to the gesture.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Drifting craft imagery */}
      <section className="pb-20" data-testid="about-marquee">
        <ImageMarquee items={craftItems} height={190} baseSpeed={16} />
      </section>

      {/* TRUSTED BY — prominent on the About page */}
      <section className="hairline-t border-subtle" data-testid="about-trusted-by">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-24">
          <Reveal>
            <div className="eyebrow text-gold mb-5 text-center md:text-left">
              In rooms like these
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-mb text-center md:text-left">
              Trusted by the firms that <span className="italic">run things.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="mt-14">
            <TrustedByMarquee variant="prominent" />
          </Reveal>
        </div>
      </section>

      <section className="hairline-t border-subtle">
        <Stagger className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-3 gap-10">
          <StaggerItem>
            <Pillar
              eyebrow="Material"
              title="Italian & Nappa only"
              body="We keep to two leathers, sourced from two tanneries, so the house voice never drifts."
            />
          </StaggerItem>
          <StaggerItem>
            <Pillar
              eyebrow="Method"
              title="Saddle-stitched, slowly"
              body="Every seam is pulled by hand. It takes longer, it lasts longer, it reads differently."
            />
          </StaggerItem>
          <StaggerItem>
            <Pillar
              eyebrow="Measure"
              title="Sets, not shelves"
              body="We release four sets and hold them. The catalogue does not grow, it deepens."
            />
          </StaggerItem>
        </Stagger>
      </section>

      {/* Parallax closing band */}
      <section
        className="hairline-t border-subtle relative overflow-hidden"
        data-testid="about-closing-band"
      >
        <LeatherBackdrop variant="stitch" intensity={0.6} drift={10} />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 py-28 md:py-40 text-center">
          <h2 className="font-display italic text-3xl md:text-5xl text-mb">
            <RevealLines lines={['The room always notices.']} className="text-center" />
          </h2>
          <Reveal delay={0.22} className="mt-12">
            <Link
              to="/sets"
              className="btn-outline-gold"
              data-magnetic
              data-testid="about-to-sets-cta"
            >
              See the sets
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const Pillar = ({ eyebrow, title, body }) => (
  <div>
    <div className="eyebrow text-gold mb-5">{eyebrow}</div>
    <h3 className="font-display text-2xl text-mb">{title}</h3>
    <p className="mt-4 text-[14px] text-mb-muted leading-relaxed">{body}</p>
  </div>
);
